import { useState, useEffect } from 'react';
import * as _ from 'lodash';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import '@vibe/core/tokens';
import { Button, Box, Flex, Heading, Icon, Divider, Label, Avatar, IconButton, Text, Search, Tooltip } from '@vibe/core';
import {
  NavigationChevronRight,
  NavigationChevronDown,
  Table,
  Play,
  Education,
  Academy,
  Calendar,
  Status,
  Warning as WarningIcon,
  ExternalPage,
} from '@vibe/icons';
import { useNavigate } from 'react-router';
import { getSubscription } from '@gorilla/common/src/lib/monday-api/api';
import { ErrorBox } from '@gorilla/ui/src/components/ErrorBox';
import { getTemplates, createTemplate, deleteTemplate, getTemplate } from '../../services/templates';
import { getExecutions } from '../../services/executions';
import { getSpreadsheetsIndex, deleteSpreadsheet, SpreadsheetsIndexEntry } from '../../services/spreadsheets';
import { mondayClient } from '../../services/monday';
import { createSignal } from '../../services/signals';
import { TemplatesList } from '../../components/TemplatesList';
import { SpreadsheetsList } from '../../components/SpreadsheetsList';
import { CreateSpreadsheetModal } from '../../components/CreateSpreadsheetModal';
import Loading from '../../components/Loading';
import { ErrorScreen } from '../../components/ErrorScreen';
import { useAllBoards } from '../../shared/hooks/use-boards';
import useMonday from '../../hooks/use-monday';
import { getDefaultTemplate } from '../../hooks/use-spreadsheet-template';
import { ExecutionsSummary } from '../../shared/components/ExecutionsSummary';
import { FormulaDocumentationModal } from '../../components/FormulaDocumentationModal';
import './HomePage.css';
import { IntroductionModal } from '../../components/IntroductionModal';
import { SpreadsheetTemplatesWhatsThisDialog } from '../../components/WhatsThisDialog';
import ScrollToTop from '../../shared/components/ScrollToTop';

type OnboardingProps = {
  title: string;
  description: string;
  children: any;
};

function Onboarding({ title, description, children }: OnboardingProps) {
  return (
    <>
      <Tooltip
        zIndex={2000}
        title={title}
        content={description}
        shouldShowOnMount
        //open
        position="right"
        moveBy={{ main: 30, secondary: 0 }}
      >
        {children}
      </Tooltip>
    </>
  );
}

function HomePage() {
  const { data: templates, error, isLoading, mutate: mutateTemplates } = useSWR('/api/get-templates', async () => getTemplates());
  const { mondayContext, appMode, subscription } = useMonday();
  const planId = subscription?.plan_id || 'premium';
  const {
    data: executionsResult,
    error: executionsError,
    isLoading: isLoadingExecutions,
  } = useSWR('/api/get-executions', async () => {
    let pId = 'premium';

    try {
      const subscription = await getSubscription(mondayClient);
      pId = subscription?.plan_id || 'premium';
    } catch (err) {
      console.log('could not fetch subscription', err);
    }

    return getExecutions(pId);
  });
  const { boards, error: boardsError } = useAllBoards();
  const navigate = useNavigate();
  const [showCreateSpreadsheetModal, setShowCreateSpreadsheetModal] = useState<any>(false);
  const [showDocumentation, setShowDocumentation] = useState<boolean>(false);
  const [showIntroductionModal, setShowIntroductionModal] = useState<boolean>(window.localStorage.getItem('seen-introduction') !== 'yes');
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 1080);
  const [showTemplates, setShowTemplates] = useState(true);
  const [showSpreadsheets, setShowSpreadsheets] = useState(false);
  const [templateSearchTerm, setTemplateSearchTerm] = useState<string>('');
  const [hideSavedSpreadsheets, setHideSavedSpreadsheets] = useState<boolean>(true);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1080);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const {
    data: spreadsheets,
    error: spreadsheetsError,
    isLoading: isLoadingSpreadsheets,
    mutate: mutateSpreadsheets,
  } = useSWR('/api/get-spreadsheets-index', async () => {
    const indexResponse = await getSpreadsheetsIndex();

    return indexResponse.value;
  });

  const {
    trigger: createTemplateTrigger,
    isMutating: isCreatingTemplate,
    error: createTemplateError,
  } = useSWRMutation('/api/create-template', async () => {
    const defaultTemplate = await getDefaultTemplate(mondayContext?.boardId!, true);
    return createTemplate(defaultTemplate);
  });

  const {
    trigger: duplicateTemplateTrigger,
    //isMutating: isDuplicatingTemplate,
    //error: duplicateTemplateError,
  } = useSWRMutation('/api/duplicate-template', async (url, { arg: template }: { arg: any }) => {
    const originalTemplate = await getTemplate(template.id);
    const newConfig = _.cloneDeep(originalTemplate.config);

    if (newConfig.messages) {
      // for now, don't copy messages
      newConfig.messages = [];
    }

    const newTemplate = {
      name: `${originalTemplate.name} (copy)`,
      type: 'spreadsheet',
      private: true,
      config: newConfig,
    };

    return createTemplate(newTemplate);
  });

  const { trigger: deleteTemplateTrigger, isMutating: isDeletingTemplate } = useSWRMutation(
    '/api/delete-template',
    (key, { arg: template }) => deleteTemplate(template),
  );

  const { trigger: deleteSpreadsheetTrigger, isMutating: isDeletingSpreadsheet } = useSWRMutation(
    '/api/delete-spreadsheet',
    (key, { arg: spreadsheet }) => deleteSpreadsheet((spreadsheet as SpreadsheetsIndexEntry).id),
  );

  if (appMode === 'beta_expired') {
    return <ErrorScreen message="The beta expired, please subscribe if you want to keep on using the app." />;
  }

  if (appMode === 'trial_expired') {
    return <ErrorScreen message="The trial expired, please subscribe if you want to keep on using the app." />;
  }

  if (_.get(mondayContext, 'user.isGuest')) {
    return <ErrorScreen message="Guest mode currently not supported." />;
  }

  if (_.get(mondayContext, 'user.isViewOnly')) {
    return <ErrorScreen message="Viewers mode currently not supported." />;
  }

  if (error) {
    return <ErrorBox error={error} mondayClient={mondayClient} />;
  }

  if (boardsError) {
    return <ErrorBox error={boardsError} mondayClient={mondayClient} />;
  }

  if (createTemplateError) {
    return <ErrorBox error={createTemplateError} mondayClient={mondayClient} />;
  }

  if (isLoading || !templates || isLoadingSpreadsheets || !boards || !mondayContext) {
    return <Loading />;
  }

  return (
    <div className="Home">
      <ScrollToTop />
      {showCreateSpreadsheetModal !== false ? (
        <CreateSpreadsheetModal
          onClose={() => setShowCreateSpreadsheetModal(false)}
          show={true}
          initialCtx={showCreateSpreadsheetModal || {}}
        />
      ) : null}
      {showDocumentation ? <FormulaDocumentationModal show={showDocumentation} onClose={() => setShowDocumentation(false)} /> : null}
      {showIntroductionModal ? (
        <IntroductionModal
          show={showIntroductionModal}
          onClose={() => {
            window.localStorage.setItem('seen-introduction', 'yes');
            setShowIntroductionModal(false);
          }}
        />
      ) : null}
      {(!templates || !templates.length) && (!spreadsheets || !spreadsheets.length) ? <></> : <></>}
      {/* <FeedbackBanner /> */}
      {executionsResult ? <ExecutionsSummary executionsResult={executionsResult} bucket={planId} /> : null}
      <div>
        <div
          style={{
            margin: '32px',
          }}
        >
          <Flex gap={0} align="stretch" direction="row">
            {/* QUICK ACTIONS */}

            <div
              style={{
                width: '100%',
              }}
            >
              <div style={{ height: '180px' }}>
                <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '24px', height: '100%' }}>
                  <div
                    className="quickaction--item"
                    onClick={async () => {
                      const newTemplate = await createTemplateTrigger();

                      if (newTemplate) {
                        mutateTemplates(templates ? [...templates, newTemplate] : [newTemplate]);
                        navigate(`/template/${newTemplate.id}/edit`);
                      }
                    }}
                  >
                    {!templates || !templates.length ? (
                      <Onboarding
                        title=""
                        description="Templates are configurations in between monday.com boards and spreadsheets. Use them to recurrently export spreadsheets manually or automatically."
                      >
                        <>
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: '50%',
                              transform: 'translate3d(-50%,-12px,0)',
                            }}
                          >
                            <Label text="Get started" color="primary" />
                          </div>
                          <Flex gap={16} justify="center" direction="column">
                            <Avatar type="icon" size="medium" withoutBorder={true} icon={Status} customBackgroundColor="#0073ea" square />
                            <div>
                              <Heading
                                type="h3"
                                style={{
                                  textAlign: 'center',
                                  color: '#0073ea',
                                }}
                              >
                                Create template
                              </Heading>
                              <Text type="text2" color="secondary" style={{ textAlign: 'center' }}>
                                and configure spreadsheet
                              </Text>
                            </div>
                          </Flex>
                        </>
                      </Onboarding>
                    ) : (
                      <>
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: '50%',
                            transform: 'translate3d(-50%,-12px,0)',
                          }}
                        >
                          <Label text="Get started" color="primary" />
                        </div>
                        <Flex gap={16} justify="center" direction="column">
                          <Avatar type="icon" size="medium" withoutBorder={true} icon={Status} customBackgroundColor="#0073ea" square />
                          <div>
                            <Heading
                              type="h3"
                              style={{
                                textAlign: 'center',
                                color: '#0073ea',
                              }}
                            >
                              Create template
                            </Heading>
                            <Text type="text2" color="secondary" style={{ textAlign: 'center' }}>
                              and configure spreadsheet
                            </Text>
                          </div>
                        </Flex>
                      </>
                    )}
                  </div>
                  {/* 
                  <div
                    className={!templates || !templates.length ? 'quickaction--item quickaction--disabled' : 'quickaction--item'}
                    onClick={async () => {
                      setShowCreateSpreadsheetModal({});
                    }}
                  >
                    {!templates || !templates.length ? (
                      <></>
                    ) : (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translate3d(-50%,-12px,0)',
                        }}
                      >
                        <Label text="Choose type" color="primary" />
                      </div>
                    )}
                    <Flex gap={16} justify="center" direction="column">
                      <Avatar
                        type="icon"
                        size="medium"
                        withoutBorder={true}
                        icon={Status}
                        customBackgroundColor={!templates || !templates.length ? '#d0d4e4' : '#0073ea'}
                        square
                      />
                      <div>
                        <Heading
                          type="h3"
                          style={{
                            textAlign: 'center',
                            color: !templates || !templates.length ? '#d0d4e4' : '#0073ea',
                          }}
                        >
                          Export spreadsheet
                        </Heading>
                        <Text
                          type="text2"
                          color="secondary"
                          style={{ textAlign: 'center', color: !templates || !templates?.length ? '#d0d4e4' : '' }}
                        >
                          from spreadsheet template
                        </Text>
                      </div>
                    </Flex>
                  </div>
                  */}
                  <div
                    className={!templates || !templates.length ? 'quickaction--item quickaction--disabled' : 'quickaction--item'}
                    onClick={async () => {
                      setShowCreateSpreadsheetModal({ formatId: 'excel' });
                    }}
                  >
                    {templates && templates.length ? (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translate3d(-50%,-12px,0)',
                        }}
                      >
                        <Label text="Popular" color="primary" />
                      </div>
                    ) : null}
                    <Flex gap={16} justify="center" direction="column">
                      <Avatar
                        type="icon"
                        size="medium"
                        withoutBorder={true}
                        icon={Table}
                        customBackgroundColor={!templates || !templates.length ? '#d0d4e4' : '#0073ea'}
                        square
                      />
                      <div>
                        <Heading
                          type="h3"
                          style={{
                            textAlign: 'center',
                            color: !templates || !templates.length ? '#d0d4e4' : '#0073ea',
                          }}
                        >
                          Export to Excel
                        </Heading>
                        <Text
                          type="text2"
                          color="secondary"
                          style={{ textAlign: 'center', color: !templates || !templates?.length ? '#d0d4e4' : '' }}
                        >
                          manually and download
                        </Text>
                      </div>
                    </Flex>
                  </div>
                  <div
                    className={!templates || !templates.length ? 'quickaction--item quickaction--disabled' : 'quickaction--item'}
                    onClick={async () => {
                      setShowCreateSpreadsheetModal({ formatId: 'csv' });
                    }}
                  >
                    <Flex gap={16} justify="center" direction="column">
                      <Avatar
                        type="icon"
                        size="medium"
                        withoutBorder={true}
                        icon={Table}
                        customBackgroundColor={!templates || !templates.length ? '#d0d4e4' : '#0073ea'}
                        square
                      />
                      <div>
                        <Heading
                          type="h3"
                          style={{
                            textAlign: 'center',
                            color: !templates || !templates.length ? '#d0d4e4' : '#0073ea',
                          }}
                        >
                          Export to CSV
                        </Heading>
                        <Text
                          type="text2"
                          color="secondary"
                          style={{ textAlign: 'center', color: !templates || !templates?.length ? '#d0d4e4' : '' }}
                        >
                          manually and download
                        </Text>
                      </div>
                    </Flex>
                  </div>
                </div>
              </div>
            </div>
          </Flex>
        </div>

        <div
          style={{
            margin: '32px',
          }}
        >
          <Flex gap={32} align="stretch" direction="column">
            {/* TEMPLATES */}
            {templates && templates.length ? (
              <div
                style={{
                  width: '100%',
                }}
              >
                <Box className="list--wrapper_" shadow="xs" rounded="medium" border borderColor="layoutBorderColor">
                  <Box padding="medium" paddingStart="large" backgroundColor="greyBackgroundColor">
                    <Flex gap="medium" align="center" direction="row">
                      <Box>
                        <Flex gap={10}>
                          <Flex gap={16}>
                            <Heading type="h3" style={{ marginLeft: '5px', fontWeight: '600' }}>
                              Spreadsheet templates
                            </Heading>
                            {/*<SpreadsheetTemplatesWhatsThisDialog />*/}
                          </Flex>
                        </Flex>
                      </Box>
                      <Box style={{ marginLeft: 'auto', width: '200px' }}>
                        <Flex gap="small" align="center" justify="end" direction="row">
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              minHeight: '40px',
                            }}
                          >
                            <Search
                              id="search-templates"
                              inputAriaLabel="Search templates"
                              placeholder="Search"
                              size="small"
                              disabled={!templates || !templates.length || templates.length < 2}
                              onChange={(value) => {
                                setTemplateSearchTerm(value);
                              }}
                            />
                          </div>
                        </Flex>
                      </Box>
                    </Flex>
                  </Box>

                  {showTemplates ? (
                    <>
                      <Divider withoutMargin />
                      <Box className="list--scroll_">
                        {templates && templates.length ? (
                          <>
                            <TemplatesList
                              templateSearchTerm={templateSearchTerm}
                              mondayContext={mondayContext}
                              templates={templates}
                              onDeleteClick={async (template) => {
                                await deleteTemplateTrigger(template);
                                mutateTemplates(templates.filter((t) => t.id !== template.id));
                              }}
                              onDuplicateClick={async (template) => {
                                const newTemplate = await duplicateTemplateTrigger(template);
                                // @ts-ignore
                                navigate(`/template/${newTemplate.id}/edit`);
                              }}
                              onExportClick={(template) => {
                                setShowCreateSpreadsheetModal({ formatId: 'excel', templateId: template.id, showSelection: true });
                              }}
                            />
                          </>
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '48px 0',
                              width: '100%',
                              height: '100%',
                              textAlign: 'center',
                            }}
                          >
                            <Flex gap={20} align="center" justify="center" direction="column">
                              <div>
                                <Heading type="h3" align="center" style={{ fontWeight: '600' }}>
                                  No templates found
                                </Heading>
                                <Text type="text2" color="secondary" style={{ marginTop: '5px' }}>
                                  Create your first template to get started.
                                </Text>
                              </div>
                              <Button
                                kind="primary"
                                loading={isCreatingTemplate}
                                onClick={async () => {
                                  const newTemplate = await createTemplateTrigger();
                                  mutateTemplates(templates ? [...templates, newTemplate] : [newTemplate]);
                                  navigate(`/template/${newTemplate.id}/edit`);
                                }}
                              >
                                Create template
                              </Button>
                            </Flex>
                          </div>
                        )}
                      </Box>
                    </>
                  ) : (
                    <></>
                  )}
                </Box>
              </div>
            ) : null}

            <div>
              {/* QUICK LINKS */}

              <div
                style={{
                  width: '100%',
                }}
              >
                <div style={{ height: '72px' }}>
                  <div
                    style={{
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'nowrap',
                      gap: '0px',
                      height: '100%',
                      border: '1px solid #d0d4e4',
                      borderRadius: '8px',
                      boxShadow: '0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <div
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'stretch',
                        flex: 1,
                      }}
                      onClick={() => {
                        createSignal('get_started_blog');
                        window.open('https://getgorilla.app/products/spreadsheet-gorilla/docs/getting-started', '_blank');
                      }}
                    >
                      <div
                        style={{
                          overflow: 'hidden',
                          position: 'relative',
                          padding: '24px 20px',
                          width: '100%',
                          backgroundColor: '#f6f7fb',
                          borderRight: '1px solid #d0d4e4',
                          color: '#323338',
                        }}
                      >
                        <Flex gap={20} justify="center" align="end">
                          <div
                            style={{
                              width: '100%',
                            }}
                          >
                            <Flex gap={15}>
                              <div>
                                <Flex gap={8}>
                                  <Icon icon={Academy} size={24} ignoreFocusStyle />
                                  <strong>Getting started</strong>
                                </Flex>
                              </div>
                            </Flex>
                            <div
                              style={{
                                position: 'absolute',
                                top: '50%',
                                right: '18px',
                                transform: 'translateY(-50%)',
                              }}
                            >
                              <IconButton icon={ExternalPage} size="small" />
                            </div>
                          </div>
                        </Flex>
                      </div>
                    </div>

                    <div
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'stretch',
                        flex: 1,
                      }}
                      onClick={() => {
                        createSignal('tutorials');
                        window.open('https://www.youtube.com/playlist?list=PLNt6YgM15GuHMoUhBM0fH1Ji_ARxdiQ2p', '_blank');
                      }}
                    >
                      <div
                        style={{
                          overflow: 'hidden',
                          position: 'relative',
                          padding: '24px 20px',
                          width: '100%',
                          backgroundColor: '#f6f7fb',
                          borderRight: '1px solid #d0d4e4',
                          color: '#323338',
                        }}
                      >
                        <Flex gap={20} justify="center" align="end">
                          <div
                            style={{
                              width: '100%',
                            }}
                          >
                            <Flex gap={15}>
                              <div>
                                <Flex gap={8}>
                                  <Icon icon={Play} size={24} ignoreFocusStyle />
                                  <strong>Video tutorials</strong>
                                </Flex>
                              </div>
                            </Flex>
                            <div
                              style={{
                                position: 'absolute',
                                top: '50%',
                                right: '18px',
                                transform: 'translateY(-50%)',
                              }}
                            >
                              <IconButton icon={ExternalPage} size="small" />
                            </div>
                          </div>
                        </Flex>
                      </div>
                    </div>

                    <div
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'stretch',
                        flex: 1,
                      }}
                      onClick={() => {
                        createSignal('documentation');
                        window.open('https://getgorilla.app/products/spreadsheet-gorilla/how-to-use', '_blank');
                      }}
                    >
                      <div
                        style={{
                          overflow: 'hidden',
                          position: 'relative',
                          padding: '24px 20px',
                          width: '100%',
                          backgroundColor: '#f6f7fb',
                          borderRight: '1px solid #d0d4e4',
                          color: '#323338',
                        }}
                      >
                        <Flex gap={20} justify="center" align="end">
                          <div
                            style={{
                              width: '100%',
                            }}
                          >
                            <Flex gap={15}>
                              <div>
                                <Flex gap={10}>
                                  <Icon icon={Education} size={24} ignoreFocusStyle />
                                  <strong>Documentation</strong>
                                </Flex>
                              </div>
                            </Flex>
                            <div
                              style={{
                                position: 'absolute',
                                top: '50%',
                                right: '18px',
                                transform: 'translateY(-50%)',
                              }}
                            >
                              <IconButton icon={ExternalPage} size="small" />
                            </div>
                          </div>
                        </Flex>
                      </div>
                    </div>

                    {!isMobile ? (
                      <div
                        style={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'stretch',
                          flex: 1,
                        }}
                        onClick={() => {
                          createSignal('book-a-demo');
                          window.open('https://cal.com/gorilla-apps/spreadsheet-gorilla-demo', '_blank');
                        }}
                      >
                        <div
                          style={{
                            overflow: 'hidden',
                            position: 'relative',
                            padding: '24px 20px',
                            width: '100%',
                            backgroundColor: '#f6f7fb',
                            color: '#323338',
                          }}
                        >
                          <Flex gap={20} justify="center" align="end">
                            <div
                              style={{
                                width: '100%',
                              }}
                            >
                              <Flex gap={15}>
                                <div>
                                  <Flex gap={10}>
                                    <Icon icon={Calendar} size={24} ignoreFocusStyle />
                                    <strong>Book a demo</strong>
                                  </Flex>
                                </div>
                              </Flex>
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '50%',
                                  right: '18px',
                                  transform: 'translateY(-50%)',
                                }}
                              >
                                <IconButton icon={ExternalPage} size="small" />
                              </div>
                            </div>
                          </Flex>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {/* SPREADSHEETS */}
            {hideSavedSpreadsheets && spreadsheets && spreadsheets.length ? (
              <div
                onClick={() => setHideSavedSpreadsheets(false)}
                style={{
                  margin: '0 0 0 auto',
                  textAlign: 'right',
                  cursor: 'pointer',
                }}
              >
                <Text type="text3" color="secondary" style={{ fontSize: '0.6rem' }}>
                  Saved spreadsheets
                </Text>
              </div>
            ) : null}

            {!hideSavedSpreadsheets && spreadsheets && spreadsheets.length ? (
              <div
                style={{
                  width: '100%',
                }}
              >
                <Box className="list--wrapper_" shadow="xs" rounded="medium" border borderColor="layoutBorderColor">
                  <Box
                    padding="medium"
                    paddingEnd="large"
                    //backgroundColor="greyBackgroundColor"
                  >
                    <Flex gap="medium" align="center" direction="row">
                      <Box>
                        <Flex gap={10}>
                          <IconButton
                            icon={showSpreadsheets ? NavigationChevronDown : NavigationChevronRight}
                            size="small"
                            kind="tertiary"
                            onClick={() => {
                              {
                                showSpreadsheets === false ? setShowSpreadsheets(true) : setShowSpreadsheets(false);
                              }
                            }}
                          />
                          <Flex gap={8}>
                            <Heading type="h3" style={{ fontWeight: '600' }}>
                              Saved spreadsheets
                            </Heading>
                          </Flex>
                        </Flex>
                      </Box>

                      <Box style={{ marginLeft: 'auto' }}>
                        <Flex gap="small" align="center" direction="row">
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              minHeight: '40px',
                            }}
                          >
                            <Label text="Deprecated" color="negative" />
                            {/*<SpreadsheetsWhatsThisDialog />*/}
                          </div>
                        </Flex>
                      </Box>
                    </Flex>
                  </Box>

                  {showSpreadsheets ? (
                    <>
                      <Divider withoutMargin />

                      <Box className="list--scroll_">
                        {spreadsheets && spreadsheets.length ? (
                          <Box>
                            <SpreadsheetsList
                              spreadsheets={spreadsheets}
                              onDeleteClick={async (spreadsheet) => {
                                await deleteSpreadsheetTrigger(spreadsheet as any); // TODO: fix any
                                mutateSpreadsheets(spreadsheets.filter((s) => s.id !== spreadsheet.id));
                                createSignal('spreadsheet_delete');
                              }}
                            />
                          </Box>
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '48px 0',
                              width: '100%',
                              height: '100%',
                              textAlign: 'center',
                            }}
                          >
                            <Flex gap={20} align="center" justify="center" direction="column">
                              <div>
                                <Heading type="h3" align="center" style={{ fontWeight: '600' }}>
                                  No spreadsheets found
                                </Heading>
                                {/* 
                                {!templates || !templates.length ? (
                                  <Text type="text2" color="secondary" style={{ marginTop: '5px' }}>
                                    Create a spreadsheet template first.
                                  </Text>
                                ) : (
                                  <Text type="text2" color="secondary" style={{ marginTop: '5px' }}>
                                    Create a spreadsheet from a template.
                                  </Text>
                                )}
                                */}
                              </div>
                              {/* 
                              <Button
                                kind="primary"
                                size="medium"
                                disabled={!templates || !templates.length}
                                onClick={async () => {
                                  setShowCreateSpreadsheetModal({ formatId: 'in-app' });
                                }}
                              >
                                Save spreadsheet
                              </Button>
                              */}
                            </Flex>
                          </div>
                        )}
                      </Box>
                    </>
                  ) : (
                    <></>
                  )}
                </Box>
              </div>
            ) : null}
          </Flex>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
